import { useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { Trash2 } from 'lucide-react';

const REVEAL = 88;
const SNAP = 48;

export default function SwipeToDelete({
  onDelete,
  disabled,
  children,
}: {
  onDelete: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  const [offset, setOffset] = useState(0);
  const startX = useRef(0);
  const startOffset = useRef(0);
  const dragging = useRef(false);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    dragging.current = true;
    startX.current = event.clientX;
    startOffset.current = offset;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const next = Math.min(REVEAL, Math.max(0, startOffset.current + startX.current - event.clientX));
    setOffset(next);
  };

  const onPointerUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    setOffset((current) => (current >= SNAP ? REVEAL : 0));
  };

  return (
    <div className="relative overflow-hidden rounded-xl">
      <button
        type="button"
        onClick={onDelete}
        disabled={disabled}
        className="absolute inset-y-0 right-0 w-[88px] bg-red-600 text-white flex flex-col items-center justify-center gap-1 text-xs font-medium"
        aria-label="Delete"
      >
        <Trash2 size={18} />
        Delete
      </button>
      <div
        className="relative bg-white rounded-xl border border-gray-200"
        style={{ transform: `translateX(-${offset}px)`, touchAction: 'pan-y' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {children}
      </div>
    </div>
  );
}
